import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../services/api';
import { useTier } from '../context/TierContext';
import { LoadingState, ErrorState, EmptyState } from '../components/primitives';
import { timeAgo } from '../lib/format';
import { SuperadminActionItem } from '../types/superadmin';

/**
 * Cross-tenant Actions Center — the superadmin-side counterpart to each
 * client's own Actions Center in admin-portal, but not scoped to one tenant:
 * this lists notifications about ANY client (starting with "a client
 * submitted their KB for review") in one place.
 */
export default function ActionsCenter() {
  const { tier } = useTier();
  const [actions, setActions] = useState<SuperadminActionItem[] | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [resolvingId, setResolvingId] = useState<string | null>(null);

  const fetchActions = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      const res = await api.getActions(tier);
      setActions(res.data.actions);
    } catch (err: any) {
      setLoadError(err?.message || 'Failed to load actions.');
    } finally {
      setLoading(false);
    }
  }, [tier]);

  useEffect(() => {
    fetchActions();
  }, [fetchActions]);

  const handleResolve = async (action: SuperadminActionItem, status: 'dismissed' | 'completed') => {
    try {
      setResolvingId(action._id);
      setActionError(null);
      await api.updateActionStatus(action._id, status);
      setActions((prev) => (prev ? prev.filter((a) => a._id !== action._id) : prev));
    } catch (err: any) {
      setActionError(err?.message || 'Failed to update action.');
    } finally {
      setResolvingId(null);
    }
  };

  if (loading) return <LoadingState />;
  if (loadError) return <ErrorState message={loadError} />;

  return (
    <div className="kb-product kb-actions-page">
      <div className="head">
        <div>
          <div className="eyebrow">Actions Center</div>
          <h1 className="title">
            <span className="serif">Things worth your attention, across every client</span>
          </h1>
          <p className="subtitle">
            Knowledge Base submissions ready for review, across every client.
          </p>
        </div>
      </div>

      {actionError && <div className="panel-note neg" role="alert">{actionError}</div>}

      <section className="section">
        <div className="section-head">
          <div>
            <h3>Open, newest first</h3>
            <p className="desc">Dismiss what doesn't need action, or mark it done once you've handled it.</p>
          </div>
        </div>
        <div className="tbl">
          <table>
            <thead>
              <tr>
                <th>Action</th>
                <th style={{ width: 180 }}>Client</th>
                <th style={{ width: 130 }}>Raised</th>
                <th style={{ width: 220 }} />
              </tr>
            </thead>
            <tbody>
              {(actions || []).map((action) => (
                <tr key={action._id}>
                  <td>
                    <div style={{ fontWeight: 500 }}>{action.title}</div>
                    {action.body && (
                      <div className="desc" style={{ marginTop: 2 }}>
                        {action.body}
                      </div>
                    )}
                  </td>
                  <td>
                    <Link
                      to={`/clients/${encodeURIComponent(action.tenantId)}`}
                      className="mono"
                      style={{ color: 'var(--ink-muted)', textDecoration: 'none' }}
                    >
                      {action.tenantId}
                    </Link>
                  </td>
                  <td>{timeAgo(action.createdAt)}</td>
                  <td>
                    {/* Reviewing (navigating to the client) is deliberately
                        NOT what marks this done — clicking through to look
                        is not the same as having actually reviewed it.
                        "Mark done" is its own explicit action, same
                        principle as the admin-portal Actions Center fix:
                        don't complete on open, only on genuine completion. */}
                    <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                      <button
                        className="btn"
                        disabled={resolvingId === action._id}
                        onClick={() => handleResolve(action, 'dismissed')}
                      >
                        Dismiss
                      </button>
                      {isSafeInternalPath(action.ctaLink) && (
                        <Link to={action.ctaLink} className="btn">
                          {action.ctaLabel || 'Review'}
                        </Link>
                      )}
                      <button
                        className="btn btn-primary"
                        disabled={resolvingId === action._id}
                        onClick={() => handleResolve(action, 'completed')}
                      >
                        Mark done
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
              {(actions || []).length === 0 && (
                <tr>
                  <td colSpan={4}>
                    <EmptyState>
                      <b>Nothing needs your attention right now.</b>
                    </EmptyState>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}

export function isSafeInternalPath(path: string | null | undefined): path is string {
  return Boolean(
    path &&
    path.startsWith('/') &&
    !path.startsWith('//') &&
    !path.includes('\\') &&
    !Array.from(path).some((character) => character.charCodeAt(0) < 32)
  );
}
